/**
 * The ALTCHA proof of work, solved in the browser with WebCrypto — a copy of `packages/widget-voice-frame/src/altcha.ts`
 * (plan-019 WID-CORE; design §31.0.3 D12: copied, never imported across packages, and kept in step by
 * `altcha-solve.test.ts`, which runs both solvers over the same challenges). An anonymous help grant and an anonymous
 * help request each carry their own single-use solve (`POST /v1/public/help/grants`, `POST /v1/public/help/requests`;
 * the api verifies it with the opt-out issuer, spent namespace `help`).
 *
 *   password(counter) = nonce bytes ‖ counter as a big-endian uint32
 *   key(counter)      = PBKDF2(password, salt bytes, cost iterations, keyLength bytes, SHA-256)
 *   solved            ⇔ hex(key) starts with keyPrefix
 *
 * The payload is `base64(JSON({ challenge: { parameters, signature }, solution: { counter, derivedKey } }))` with the
 * parameters exactly as they arrived. It lives outside the launcher chunk: the panel and the hosts reach it through
 * `@wetalk/help-widget/anonymous`.
 */
/** The only algorithm the api issues and accepts (`ALTCHA_ALGORITHM`). */
export const ALTCHA_ALGORITHM = "PBKDF2/SHA-256";
/** How many derivations are in flight at once. */
const CONCURRENT_DERIVATION = 4;
/** The counter is a uint32; past it there is no answer to find. */
const COUNTER_MAX = 0xffff_ffff;
const HEX_PAIRS = /^(?:[0-9a-f]{2})+$/;
function bytesOfHex(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i += 1)
        bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    return bytes;
}
function hexOf(bytes) {
    let hex = "";
    for (const byte of bytes)
        hex += byte.toString(16).padStart(2, "0");
    return hex;
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** The challenge inside the api's `{ data }` envelope, or `null` when it is not one this solver can do. */
export function challengeOf(body) {
    if (!isRecord(body) || !isRecord(body["data"]))
        return null;
    const data = body["data"];
    const parameters = data["parameters"];
    const signature = data["signature"];
    if (!isRecord(parameters) || typeof signature !== "string" || signature === "")
        return null;
    return parametersOf(parameters) === null ? null : { parameters, signature };
}
function parametersOf(raw) {
    const { algorithm, nonce, salt, cost, keyLength, keyPrefix } = raw;
    if (algorithm !== ALTCHA_ALGORITHM ||
        typeof nonce !== "string" ||
        !HEX_PAIRS.test(nonce) ||
        typeof salt !== "string" ||
        !HEX_PAIRS.test(salt) ||
        typeof cost !== "number" ||
        !Number.isInteger(cost) ||
        cost < 1 ||
        typeof keyLength !== "number" ||
        !Number.isInteger(keyLength) ||
        keyLength < 1 ||
        typeof keyPrefix !== "string" ||
        !/^[0-9a-f]*$/.test(keyPrefix)) {
        return null;
    }
    return { nonce: bytesOfHex(nonce), salt: bytesOfHex(salt), cost, keyLength, keyPrefix };
}
async function derive(subtle, parameters, counter) {
    const password = new Uint8Array(parameters.nonce.length + 4);
    password.set(parameters.nonce, 0);
    new DataView(password.buffer).setUint32(parameters.nonce.length, counter, false);
    const key = await subtle.importKey("raw", password, "PBKDF2", false, ["deriveBits"]);
    const bits = await subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt: parameters.salt, iterations: parameters.cost }, key, parameters.keyLength * 8);
    return hexOf(new Uint8Array(bits));
}
/**
 * Finds the first counter whose derived key starts with the prefix. Resolves `null` when
 * `shouldStop()` turns true (the visitor hung up) or the counter space is exhausted. Never rejects for
 * a well-formed challenge; a WebCrypto failure rejects.
 */
export async function solveAltcha(subtle, challenge, shouldStop) {
    const parameters = parametersOf(challenge.parameters);
    if (parameters === null)
        return null;
    for (let start = 0; start <= COUNTER_MAX; start += CONCURRENT_DERIVATION) {
        if (shouldStop())
            return null;
        const counters = [];
        for (let counter = start; counter < start + CONCURRENT_DERIVATION && counter <= COUNTER_MAX; counter += 1) {
            counters.push(counter);
        }
        const keys = await Promise.all(counters.map((counter) => derive(subtle, parameters, counter)));
        for (let i = 0; i < keys.length; i += 1) {
            const key = keys[i];
            const counter = counters[i];
            if (key !== undefined && counter !== undefined && key.startsWith(parameters.keyPrefix)) {
                return { counter, derivedKey: key };
            }
        }
    }
    return null;
}
/** The `altcha` field of the grant: base64 of the JSON the api decodes. */
export function altchaPayloadOf(challenge, solution) {
    const json = JSON.stringify({
        challenge: { parameters: challenge.parameters, signature: challenge.signature },
        solution: { counter: solution.counter, derivedKey: solution.derivedKey },
    });
    const bytes = new TextEncoder().encode(json);
    let binary = "";
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary);
}
//# sourceMappingURL=altcha-solve.js.map